import { Forward, Hash, MessageCircle, Search, Users } from '@/components/ui/icons';
import { useMemo, useState } from 'react';
import { LIMITS, type MessageDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { messageApi } from '@/api/endpoints';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { conversationTitle } from '@/lib/conversations';
import { cn } from '@/lib/utils';
import { useServers } from '@/stores/servers';
import { useConversations } from '@/stores/social';
import { toast } from '@/stores/ui';

interface Destination {
  key: string;
  label: string;
  sub: string;
  kind: 'channel' | 'dm' | 'group';
  target: { channelId: string } | { conversationId: string };
  /** Most recent first. */
  order: number;
}

/** Forward a message to one of your DMs or channels, with an optional comment. */
export function ForwardModal({ message, onClose }: { message: MessageDTO; onClose: () => void }) {
  const servers = useServers((s) => s.servers);
  const channels = useServers((s) => s.channels);
  const conversations = useConversations((s) => s.conversations);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Destination | null>(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  const destinations = useMemo(() => {
    const list: Destination[] = [];
    for (const c of Object.values(conversations)) {
      list.push({
        key: `c:${c.id}`,
        label: conversationTitle(c),
        sub: c.type === 'GROUP' ? 'Group' : 'Direct message',
        kind: c.type === 'GROUP' ? 'group' : 'dm',
        target: { conversationId: c.id },
        order: Date.parse(c.lastMessageAt ?? c.createdAt) || 0,
      });
    }
    for (const ch of Object.values(channels)) {
      const server = servers[ch.serverId];
      if (!server || ch.type !== 'TEXT') continue;
      list.push({ key: `s:${ch.id}`, label: `#${ch.name}`, sub: server.name, kind: 'channel', target: { channelId: ch.id }, order: Date.parse(ch.lastMessageAt ?? '') || 0 });
    }
    return list.sort((a, b) => b.order - a.order);
  }, [servers, channels, conversations]);

  const q = query.trim().toLowerCase();
  const shown = (q ? destinations.filter((d) => d.label.toLowerCase().includes(q) || d.sub.toLowerCase().includes(q)) : destinations).slice(0, 40);

  const send = async () => {
    if (!picked) return;
    setSending(true);
    try {
      await messageApi.forward(message.id, { ...picked.target, comment: comment.trim() });
      toast.success(`Forwarded to ${picked.label}.`);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
      setSending(false);
    }
  };

  const original = message.forwarded && !message.content && message.attachments.length === 0 ? message.forwarded : null;
  const previewText = original ? original.content : message.content;

  return (
    <Modal
      open
      onClose={onClose}
      title="Forward message"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button icon={<Forward size={16} />} loading={sending} disabled={!picked} onClick={() => void send()}>
            {picked ? `Send to ${picked.label}` : 'Send'}
          </Button>
        </>
      }
    >
      <div className="mb-3 flex gap-2.5 rounded-xl border border-line bg-sunken p-2.5">
        <Avatar user={message.author} size={24} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold">{original?.author.displayName ?? message.author?.displayName ?? 'Deleted User'}</p>
          <p className="line-clamp-2 text-sm text-fg-soft">{previewText || (message.attachments.length ? `📎 ${message.attachments.length} attachment(s)` : 'A message')}</p>
        </div>
      </div>
      <Input
        aria-label="Search your DMs and channels"
        placeholder="Search your DMs and channels"
        leading={<Search size={16} />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        data-autofocus
      />
      <ul className="mt-2 max-h-64 overflow-y-auto" role="listbox" aria-label="Where to forward it">
        {shown.length === 0 && <li className="px-2 py-6 text-center text-sm text-fg-muted">Nothing matches “{query}”.</li>}
        {shown.map((d) => (
          <li key={d.key}>
            <button
              type="button"
              role="option"
              aria-selected={picked?.key === d.key}
              onClick={() => setPicked(d)}
              className={cn('flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors', picked?.key === d.key ? 'bg-accent-soft' : 'hover:bg-hover')}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sunken text-fg-muted">
                {d.kind === 'channel' ? <Hash size={16} /> : d.kind === 'group' ? <Users size={16} /> : <MessageCircle size={16} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{d.label}</span>
                <span className="block truncate text-xs text-fg-muted">{d.sub}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      <Input
        className="mt-3"
        aria-label="Add a comment"
        placeholder="Add a comment (optional)"
        maxLength={LIMITS.messageMax}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && picked) {
            e.preventDefault();
            void send();
          }
        }}
      />
    </Modal>
  );
}
